import * as React from "react";
import { useState, useMemo } from "react";
import styled from "styled-components";
import { ControlLabel, MenuItem, Button, DeleteMenuItem } from "components/bootstrap";
import { Spinner, MultiSelect, Icon } from "components/common";
import useScopePermissions from "hooks/useScopePermissions";
import { useModal, ConfirmDialog } from "security-app/components/common/Modal";
import { useSelectedRows, useSelectedRowsDispatch } from "common/contexts";
import { useBulkSigmaOperations, useBulkAddNotifications, useGetNotifications } from "security-app/hooks/useSigmaAPI";
import EnterpriseApiRoutes from "common/ApiRoutes";
import { qualifyUrl } from "util/URLUtils";
import { DropdownMenu } from "security-app/components/common";
import DeleteMessage from "./DeleteMessage";
const StyledInput = styled.div`
  padding: 0.5rem 0;
`;
const WarningMessage = styled.small`
  color: ${({ theme }) => theme.colors.variant.warning};
`;
const BulkActionsButtons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;
const IconContainer = styled.div`
  width: 16px;
  display: inline-block;
  text-align: left;
`;
function BulkActions({ visibleBulkActions = ["enable", "disable", "add_notification", "download", "delete"] }) {
  const { checkPermissions } = useScopePermissions();
  const [showActionsDialog, toggleActionsDialog] = useModal();
  const [showNotificationsDialog, toggleNotificationsDialog] = useModal();
  const [selectedAction, setSelectedAction] = useState("");
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const { notifications } = useGetNotifications(true);
  const notificationOptions = useMemo(
    () => notifications.map((notification) => ({
      label: notification.title,
      value: notification.id
    })),
    [notifications]
  );
  const selectedRules = useSelectedRows();
  const selectedRulesDispatch = useSelectedRowsDispatch();
  const { bulkSigmaOperations, isMutating } = useBulkSigmaOperations();
  const { bulkAddNotifications, isAdding } = useBulkAddNotifications();
  const clearSelection = () => selectedRulesDispatch({ type: "clear" });
  const getRulesToDelete = () => {
    const mutableRules = selectedRules.filter(checkPermissions);
    const immutableRules = selectedRules.filter((rule) => !checkPermissions(rule));
    return [mutableRules, immutableRules];
  };
  const handleConfirm = () => {
    const ids = selectedAction === "delete" ? getRulesToDelete()[0].map((rule) => rule.id) : selectedRules.map((rule) => rule.id);
    switch (selectedAction) {
      case "enable":
        bulkSigmaOperations({ ids, action: "enable" }, { onSuccess: clearSelection });
        break;
      case "disable":
        bulkSigmaOperations({ ids, action: "disable" }, { onSuccess: clearSelection });
        break;
      case "delete":
        bulkSigmaOperations({ ids, action: "delete" }, { onSuccess: clearSelection });
        break;
      case "add_notifications":
        bulkAddNotifications(
          { ids, action: "add_notifications", notifications: selectedNotifications },
          { onSuccess: clearSelection }
        );
        break;
      case "download":
        const idParams = ids.map((ruleId) => `ruleIds=${ruleId}`).join("&");
        window.open(qualifyUrl(EnterpriseApiRoutes.SecurityApp.downloadSigmaRule(idParams).url), "_self");
        break;
      default:
        break;
    }
    toggleActionsDialog(false);
    toggleNotificationsDialog(false);
  };
  const onEnable = () => {
    toggleActionsDialog(true);
    setSelectedAction("enable");
  };
  const onDisable = () => {
    toggleActionsDialog(true);
    setSelectedAction("disable");
  };
  const onNotificationAdd = () => {
    toggleNotificationsDialog(true);
    setSelectedAction("add_notifications");
  };
  const onDownload = () => {
    toggleActionsDialog(true);
    setSelectedAction("download");
  };
  const onDelete = () => {
    toggleActionsDialog(true);
    setSelectedAction("delete");
  };
  const handleCancel = () => {
    toggleActionsDialog(false);
    toggleNotificationsDialog(false);
    setSelectedAction("");
  };
  const onClearSelection = (e) => {
    e.stopPropagation();
    selectedRulesDispatch({ type: "clear" });
  };
  const actionIsVisible = (actionName) => visibleBulkActions.includes(actionName);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    DropdownMenu,
    {
      trigger: /* @__PURE__ */ React.createElement(BulkActionsButtons, null, /* @__PURE__ */ React.createElement(Button, { disabled: selectedRules.length < 1 }, /* @__PURE__ */ React.createElement(IconContainer, null, /* @__PURE__ */ React.createElement(Icon, { name: "arrow_drop_down", size: "xs" })), "Bulk Actions ", selectedRules.length > 0 ? selectedRules.length : null), selectedRules.length > 0 && /* @__PURE__ */ React.createElement(Button, { onClick: onClearSelection, title: "Clear selection" }, /* @__PURE__ */ React.createElement(Icon, { name: "close" })))
    },
    actionIsVisible("enable") && /* @__PURE__ */ React.createElement(MenuItem, { onClick: onEnable }, "Enable"),
    actionIsVisible("disable") && /* @__PURE__ */ React.createElement(MenuItem, { onClick: onDisable }, "Disable"),
    actionIsVisible("add_notification") && /* @__PURE__ */ React.createElement(MenuItem, { onClick: onNotificationAdd }, "Add Notification"),
    actionIsVisible("download") && /* @__PURE__ */ React.createElement(MenuItem, { onClick: onDownload }, "Download"),
    actionIsVisible("delete") && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(MenuItem, { divider: true }), /* @__PURE__ */ React.createElement(DeleteMenuItem, { onClick: onDelete }))
  ), /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      show: showActionsDialog,
      onConfirm: handleConfirm,
      confirmText: isMutating ? /* @__PURE__ */ React.createElement(Spinner, { text: `${selectedAction}ing...`, delay: 0 }) : `${selectedAction.charAt(0).toUpperCase()}${selectedAction.slice(1)} ${selectedRules.length > 1 ? "rules" : "rule"}`,
      confirmDisabled: isMutating,
      onCancel: handleCancel,
      title: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("span", null, `${selectedAction.charAt(0).toUpperCase()}${selectedAction.slice(1)} Sigma ${selectedRules.length > 1 ? "Rules" : "Rule"}`), /* @__PURE__ */ React.createElement("br", null), selectedAction === "enable" && selectedRules.length > 1 && /* @__PURE__ */ React.createElement(WarningMessage, null, /* @__PURE__ */ React.createElement("i", null, /* @__PURE__ */ React.createElement(Icon, { name: "warning" }), " Enabling many rules at the same time can be a resource intensive task. Proceed with caution.")))
    },
    selectedAction === "delete" ? /* @__PURE__ */ React.createElement(DeleteMessage, { mutableRules: getRulesToDelete()[0], immutableRules: getRulesToDelete()[1] }) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("b", null, "Are you sure you want to ", selectedAction, " the following ", selectedRules.length > 1 ? "rules" : "rule", ":"), selectedRules.map((rule) => /* @__PURE__ */ React.createElement("span", { key: rule.id }, rule.title)))
  ), /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      show: showNotificationsDialog,
      onConfirm: handleConfirm,
      confirmText: isAdding ? /* @__PURE__ */ React.createElement(Spinner, { text: "Adding...", delay: 0 }) : "Add notification",
      confirmDisabled: isAdding || selectedNotifications.length === 0,
      onCancel: handleCancel,
      title: `Add Notification To Sigma ${selectedRules.length > 1 ? "Rules" : "Rule"}`
    },
    /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, "Add notifications to these selected rules:"), selectedRules.map((rule) => /* @__PURE__ */ React.createElement("p", { key: rule.id }, rule.title)), /* @__PURE__ */ React.createElement(StyledInput, null, /* @__PURE__ */ React.createElement(ControlLabel, null, "Notifications"), /* @__PURE__ */ React.createElement(
      MultiSelect,
      {
        id: "filter-notifications",
        onChange: (selected) => setSelectedNotifications(selected === "" ? [] : selected.split(",")),
        options: notificationOptions,
        value: (selectedNotifications != null ? selectedNotifications : []).join(",")
      }
    )))
  ));
}
export default BulkActions;
